import React, { ChangeEvent, useEffect } from 'react'
import { Heading, TitleContainer, Title, Container, SpinnerContainer, FormContainer } from './PaymentEmail.style'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import Spinner from 'parkdepot-shared/components/Spinner'
import Input from 'parkdepot-shared/components/Input/Input'
import { validateEmail } from '@utils/validateEmail.utils'
import { FormControl } from 'parkdepot-shared/components/FormControl'
import useCarpayPaymentFlow from '@hooks/useCarpayPaymentFlow'
import Error from '@components/Error/Error.component'
import { useTranslation } from 'react-i18next'
import { carpayPaymentMethodRoute } from '../PaymentMethod/route.const'
import { getFragmentData } from 'parkdepot-shared/gql'
import { OrderResponseFragment } from '@graphql/queries/getOpenOrders.query'
import useCarpayOrder from '@hooks/useCarpayOrder.hook'
import CreateAccountCTA from '@components/CreateAccountCTA/CreateAccountCTA.component'
import { useAuth } from '@context/AuthContext/AuthContext'

const CarpayPaymentEmail = () => {
    const history = useHistory()
    const location = useLocation()
    const { isLoggedIn, user } = useAuth()
    const queryparams = new URLSearchParams(location.search)
    const { state, updateState } = useCarpayPaymentFlow()
    const { t } = useTranslation()
    const { order_id } = useParams<{ order_id: string }>()
    const plate = queryparams.get('plate')

    const { data, loading, error } = useCarpayOrder(plate)

    const emailError = state.email ? !validateEmail(state.email) : false

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        updateState({ email: e.currentTarget.value })
    }

    const handleNextStep = (resetEmail = false) => {
        if (resetEmail) {
            updateState({ email: '' })
        }
        history.push(`${carpayPaymentMethodRoute(order_id)}?${queryparams.toString()}`)
    }

    const submitForm = () => {
        if (emailError) return

        handleNextStep()
    }

    useEffect(() => {
        if (user?.email) {
            updateState({ email: user.email })
        }
    }, [user?.email])

    if (loading) {
        return (
            <SpinnerContainer>
                <Spinner theme="primary" size="xl" />
            </SpinnerContainer>
        )
    }

    if (error) {
        return <Error />
    }

    if (data) {
        const orders = data!.b2c_get_open_orders.orders.map((data) => getFragmentData(OrderResponseFragment, data))

        const selectedOrder = orders.find((order) => order.order.order_id === order_id)

        if (!selectedOrder) return <div>{t('carpay.notFound.orderNotFound')}</div>
    }

    return (
        <Container>
            <TitleContainer>
                <Heading>{t('carpay.paymentEmail.title')}</Heading>
                <Title>{t('carpay.paymentEmail.subtitle')}</Title>
            </TitleContainer>

            <FormContainer onSubmit={() => null}>
                <div>
                    <FormControl label={t('carpay.paymentEmail.email')} caption={t('carpay.paymentEmail.emailCaption')}>
                        <Input
                            type="email"
                            placeholder={t('carpay.paymentEmail.email')}
                            value={state.email}
                            error={emailError}
                            onChange={handleEmailChange}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    submitForm()
                                }
                            }}
                        />
                    </FormControl>
                </div>

                <Button
                    id="confirm-email-button"
                    $kind={BUTTON_KIND.primary}
                    $size={BUTTON_SIZE.medium}
                    type="submit"
                    disabled={!state.email || emailError}
                    onClick={submitForm}
                >
                    {t('carpay.paymentEmail.confirm')}
                </Button>
                <Button
                    data-testid="skip-email-button"
                    id="skip-email-button"
                    $kind={BUTTON_KIND.secondary}
                    $size={BUTTON_SIZE.medium}
                    type="submit"
                    onClick={() => handleNextStep(true)}
                >
                    {t(isLoggedIn ? 'carpay.paymentEmail.skip' : 'accountCreationAd.continueWithoutAccount')}
                </Button>
            </FormContainer>
            <CreateAccountCTA returnUrl={encodeURIComponent(window.location.pathname + window.location.search)} />
        </Container>
    )
}

export default CarpayPaymentEmail
